import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { LoadingState, RouteErrorBoundary } from "@/components/feedback";
import { RequireAuth } from "@/features/auth/RequireAuth";
import { FieldLayout } from "@/layouts/FieldLayout";
import { PanelLayout } from "@/layouts/PanelLayout";
import NotFoundPage from "@/pages/NotFound";
import { APP_ROUTES } from "./route-paths.mjs";

// Code-splitting por rota (React.lazy): cada tela vira um chunk próprio,
// carregado só quando a rota é visitada. Os `const X = lazy(...)` ficam no
// nível do módulo (nunca dentro do componente `AppRouter`) — chamar `lazy()`
// a cada render criaria um componente novo a cada navegação e derrubaria o
// estado/remontaria a tela toda vez.
const LoginPage = lazy(() => import("@/pages/Login"));
// App do participante: publico, sem layout de painel, sem RequireAuth.
const ParticipantAppPage = lazy(() => import("@/pages/App"));

const VisaoGeralPage = lazy(() => import("@/pages/VisaoGeral"));
const EventoPage = lazy(() => import("@/pages/Evento"));
const AgendaPage = lazy(() => import("@/pages/Agenda"));
const CerebroIAPage = lazy(() => import("@/pages/CerebroIA"));
const AtendimentoPage = lazy(() => import("@/pages/Atendimento"));
const ParticipantesPage = lazy(() => import("@/pages/Participantes"));
const ExpositoresPage = lazy(() => import("@/pages/Expositores"));
const RelatoriosPage = lazy(() => import("@/pages/Relatorios"));
const UsuariosPage = lazy(() => import("@/pages/Usuarios"));
const AdminPage = lazy(() => import("@/pages/Admin"));
// Mantido comentado junto com a rota bloqueada (ver bloco "WhatsApp & templates" abaixo).
// const WhatsappPage = lazy(() => import("@/pages/Whatsapp"));

const KioskPage = lazy(() => import("@/pages/Kiosk"));
const ParticipantPage = lazy(() => import("@/pages/Participant"));
const ExhibitorPage = lazy(() => import("@/pages/Exhibitor"));

const PocWhatsappPage = lazy(() => import("@/pages/PocWhatsapp"));

/** Suspense (fallback = `LoadingState` da Foundation) + `RouteErrorBoundary`
 *  por rota, como pedido no README da Foundation — um erro de render numa
 *  tela não deve derrubar sidebar/topbar (isso já também é reforçado a nível
 *  de layout em `PanelLayout`/`FieldLayout`). */
function page(element: ReactNode): ReactNode {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<LoadingState label="Carregando página…" className="min-h-[50vh]" />}>
        {element}
      </Suspense>
    </RouteErrorBoundary>
  );
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/visao-geral" replace />} />

      {/* PUBLICA — deliberadamente ACIMA do <Route element={<RequireAuth />}>.
          O participante do evento nao tem conta; o painel continua trancado. */}
      <Route path="app" element={page(<ParticipantAppPage />)} />

      <Route path="login" element={page(<LoginPage />)} />

      <Route element={<RequireAuth />}>
        <Route element={<PanelLayout />}>
          <Route path="visao-geral" element={page(<VisaoGeralPage />)} />
          <Route path="evento" element={page(<EventoPage />)} />
          <Route path="agenda" element={page(<AgendaPage />)} />
          <Route path="cerebro-ia" element={page(<CerebroIAPage />)} />
          <Route path="atendimento" element={page(<AtendimentoPage />)} />
          <Route path="participantes" element={page(<ParticipantesPage />)} />
          <Route path="expositores" element={page(<ExpositoresPage />)} />
          <Route path="relatorios" element={page(<RelatoriosPage />)} />
          <Route path="usuarios" element={page(<UsuariosPage />)} />
          <Route path="admin" element={page(<AdminPage />)} />
          {/* Módulo "WhatsApp & templates" ocultado a pedido do cliente.
              O legado não apenas some com o item de menu (comentado em
              app/web/js/app.js) — ele também bloqueia o acesso direto, com
              `location.replace("visao-geral.html")` na primeira linha do
              <head> de app/web/whatsapp.html. Portamos os dois gates.

              A tela em si continua no repositório e funcional (pages/Whatsapp).
              Para reativar o módulo: troque este Navigate pelo elemento
              comentado abaixo e descomente o item em router/nav-config.ts. */}
          <Route path="whatsapp" element={<Navigate to="/visao-geral" replace />} />
          {/* <Route path="whatsapp" element={page(<WhatsappPage />)} /> */}
        </Route>

        <Route element={<FieldLayout />}>
          <Route path="kiosk" element={page(<KioskPage />)} />
          <Route path="participant" element={page(<ParticipantPage />)} />
          <Route path="exhibitor" element={page(<ExhibitorPage />)} />
        </Route>

        {/* Sem layout — tela cheia, própria (porta o standalone
            poc-whatsapp.html do legado). */}
        <Route path="poc-whatsapp" element={page(<PocWhatsappPage />)} />
      </Route>

      {/* Compatibilidade com URLs antigas: /<rota>.html -> /<rota>,
          client-side (replace, não some do histórico de forma estranha).
          Gerado a partir da mesma lista usada por scripts/emit-routes.mjs
          para nunca dessincronizar. */}
      {APP_ROUTES.filter((route) => route !== "/").map((route) => (
        <Route
          key={route}
          path={`${route.slice(1)}.html`}
          element={<Navigate to={route} replace />}
        />
      ))}

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
